import { useEffect, useState } from 'react';

/**
 * useTablePreferences - Saves and loads table preferences to/from localStorage
 * 
 * Saves:
 * - Column visibility (show/hide)
 * - Column order (drag & drop)
 * - Column sizing (resize)
 * - Filter state (optional)
 * 
 * @param {string} tableId - Unique identifier for this table
 * @param {object} defaultState - Default state if no saved preferences
 * @returns {object} { state, savePreferences, loadPreferences, resetPreferences }
 */

export function useTablePreferences(tableId, defaultState = {}) {
  const storageKey = `table-prefs-${tableId}`;

  const [preferences, setPreferences] = useState(() => {
    // Load from localStorage on mount
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return { ...defaultState, ...parsed };
      }
    } catch (error) {
      console.error('Error loading table preferences:', error);
    }
    return defaultState;
  });

  // Save directly to localStorage without triggering state update
  const savePreferences = (newPrefs) => {
    try {
      const updated = { ...preferences, ...newPrefs };
      localStorage.setItem(storageKey, JSON.stringify(updated));
      setPreferences(updated);
    } catch (error) {
      console.error('Error saving table preferences:', error);
    }
  };

  const loadPreferences = () => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (error) {
      console.error('Error loading table preferences:', error);
    }
    return defaultState;
  };

  const resetPreferences = () => {
    setPreferences(defaultState);
    try {
      localStorage.removeItem(storageKey);
    } catch (error) {
      console.error('Error resetting table preferences:', error);
    }
  };

  return {
    preferences,
    savePreferences,
    loadPreferences,
    resetPreferences,
  };
}

export default useTablePreferences;

